import { FileDownload } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- standalone client-side history view, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { addMonths, parse } from 'date-fns';
import { List } from 'immutable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { getExcelFile } from '@sophocles/utils';
import { formatDate, formatDateForSubmission } from '@sophocles/utils/formatUtils';
import PopPdfPopover from '@sophocles/views/sites/components/PopPdfPopover';
import { createPdf } from '@sophocles/views/sites/components/PopPdfPopover/pdf';

function PopGrid({ customer, selectedPlant, selectedStructure, siteId, pops, user, fetchPOP }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const [startDate, setStartDate] = useState(() => addMonths(new Date(), -1));
  const [endDate, setEndDate] = useState(() => new Date());

  const isBillboard = customer.get('is_billboard', true);

  // Customer-type relay-load term for the load-name column. Falls back to the
  // billboard/commercial Panel/Zone labels when no scope customer resolves — this
  // grid also mounts on the non-scoped legacy /pop route.
  const relayLoadTerm = useRelayLoadTerm(
    isBillboard
      ? { singularKey: 'reports.panel', pluralKey: 'reports.panels' }
      : { singularKey: 'reports.zone', pluralKey: 'reports.zones' },
  );
  const popList = useMemo(() => (typeof pops !== 'undefined' ? pops.toList() : List()), [pops]);

  useEffect(() => {
    fetchPOP(siteId, formatDateForSubmission(startDate), formatDateForSubmission(endDate));
  }, [siteId, startDate, endDate, fetchPOP]);

  const handleDateRangeChange = useCallback((start, end) => {
    if (start) setStartDate(start);
    if (end) setEndDate(end);
  }, []);

  const rows = useMemo(
    () =>
      popList.toArray().map((pop, idx) => ({
        id: pop.get('id', idx),
        report_date: pop.get('report_date'),
        relay_number: pop.get('relay_number'),
        relay_load_name: pop.get('relay_load_name'),
        schedules: pop.get('schedules'),
        alarm_display: pop.get('alarm_display'),
        legacy_alarm_string: pop.get('legacy_alarm_string'),
        passFail: pop.get('passFail'),
        _pop: pop,
      })),
    [popList],
  );

  const columns = useMemo(
    () => [
      {
        field: 'report_date',
        headerName: t('reports.reportDate'),
        width: 140,
        valueFormatter: value => {
          if (!value) return '';
          const date = parse(value, 'yyyy-MM-dd', new Date());
          return formatDate(date, user);
        },
      },
      {
        field: 'relay_number',
        headerName: t('sites.relay'),
        width: 80,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'relay_load_name',
        headerName: relayLoadTerm.singular,
        width: 200,
      },
      {
        field: 'schedules',
        headerName: t('general.schedules'),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'alarm_display',
        headerName: t('general.alarms'),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'passFail',
        headerName: t('general.status'),
        width: 100,
        renderCell: params =>
          params.value === 'PASS' ? (
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
              {t('sites.PASS')}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
              {t('sites.FAIL')}
            </Typography>
          ),
      },
    ],
    [t, relayLoadTerm, user],
  );

  const handleExcelExport = useCallback(() => {
    const title = 'pop-report-' + Date.now();
    const headers = [
      [t('sites.proofOfPerformance')],
      [`${t('siteGroups.plant')}: ` + selectedPlant.get('name', t('sites.popExport.noPlant'))],
      [`${t('sites.site')}: ` + selectedStructure.get('name', t('sites.popExport.noSite'))],
      [],
      [
        t('reports.reportDate'),
        `${t('sites.relay')} #`,
        relayLoadTerm.singular,
        t('general.schedules'),
        t('general.alarms'),
        t('general.status'),
      ],
    ];
    const csvColumns = [
      'report_date',
      'relay_number',
      'relay_load_name',
      'schedules',
      'legacy_alarm_string',
      'passFail',
    ];
    getExcelFile(popList, csvColumns, headers, title);
  }, [popList, selectedPlant, selectedStructure, relayLoadTerm, t]);

  const handleCreatePdf = useCallback(
    (passedPopList, relayLoadId) => {
      const filtered = passedPopList.filter(pop => pop.get('relay_load', 0) == relayLoadId);
      const relayLoadName = filtered.isEmpty()
        ? t('sites.popExport.empty')
        : filtered.first().get('relay_load_name', t('sites.popExport.noName'));
      const siteName = selectedStructure.get('name', '');
      const siteGroupName =
        selectedPlant.get('name', t('sites.popExport.noNameSiteGroup')) +
        ' - ' +
        selectedPlant.get('description', t('sites.popExport.noDescription'));
      const customerName = customer.get('name', t('sites.popExport.noName'));
      createPdf(customerName, siteGroupName, siteName, relayLoadName, startDate, endDate, filtered, isBillboard, t);
    },
    [customer, selectedPlant, selectedStructure, startDate, endDate, isBillboard, t],
  );

  const relayLoads = useMemo(() => {
    const relays = selectedStructure.getIn(['relay_controller', 'relays'], List());
    let loads = List();
    relays.forEach(relay => {
      relay.get('relay_loads', List()).forEach(load => {
        if (popList.some(value => value.get('relay_load', 0) == load.get('id', -1))) {
          loads = loads.push(load);
        }
      });
    });
    return loads;
  }, [selectedStructure, popList]);

  const toolbarContent = useMemo(
    () => (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ),
    [startDate, endDate, handleDateRangeChange],
  );

  const toolbarActions = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <PopPdfPopover isBillboard={isBillboard} onChoice={handleCreatePdf} relayLoads={relayLoads} data={popList} />
        <Tooltip title={t('actions.export', { defaultValue: 'Export' })}>
          <span>
            <IconButton size="small" onClick={handleExcelExport}>
              <FileDownload fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    [isBillboard, handleCreatePdf, relayLoads, popList, handleExcelExport, t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <DataGridToolbar
        apiRef={apiRef}
        showQuickFilter
        toolbarContent={toolbarContent}
        toolbarActions={toolbarActions}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        hideFooter
        initialState={{ sorting: { sortModel: [{ field: 'report_date', sort: 'desc' }] } }}
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default PopGrid;
